import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, testConnection } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiConnected, setApiConnected] = useState(false);

  // Check API connection and auth status on app start
  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      // Test API connection first
      const connectionTest = await testConnection();
      setApiConnected(connectionTest.success);
      
      if (!connectionTest.success) {
        setError('Cannot connect to API server. Make sure backend is running on http://localhost:5000');
        setLoading(false);
        return;
      }

      // Check if user is already logged in
      const token = localStorage.getItem('popmap_token');
      if (token) {
        try {
          const response = await authAPI.getProfile();
          const userData = response.data.data.user;
          setUser(userData);
        } catch (error) {
          console.error('Token validation failed:', error);
          // Clear invalid auth data
          localStorage.removeItem('popmap_token');
          localStorage.removeItem('popmap_user');
        }
      }
    } catch (error) {
      console.error('App initialization failed:', error);
      setError('Failed to initialize app');
    } finally {
      setLoading(false);
    }
  };

  const login = async (googleData) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Attempting login with:', googleData);
      const response = await authAPI.googleLogin(googleData);
      
      const { user: userData, token } = response.data.data;

      // Store auth data
      localStorage.setItem('popmap_token', token);
      localStorage.setItem('popmap_user', JSON.stringify(userData));
      
      setUser(userData);
      console.log('Login successful:', userData);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error.response?.data?.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint (optional)
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local auth data regardless of API call result
      localStorage.removeItem('popmap_token');
      localStorage.removeItem('popmap_user');
      setUser(null);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    apiConnected,
    login,
    logout,
    clearError,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};